import { Colors } from '@oy/uicore';
import { SystemIcon } from '@oy/uiicon';
import React from 'react';
import toastModule, {
  Toaster,
  type DefaultToastOptions,
  type Renderable,
  type Toast,
  type ToastOptions,
  type ValueOrFunction,
} from 'react-hot-toast';
import { formatString, makeId } from '../../../utilities/idComponent';

export const AppToaster = () => {
  return (
    <Toaster
      toastOptions={{
        style: {
          borderRadius: '4px',
          boxShadow: '0px 2px 6px 0px rgba(30, 43, 55, 0.50)',
          padding: '8px 12px',
        },
        success: {
          icon: (
            <SystemIcon name="checkmark-selected" color={Colors.GREEN100} />
          ),
          style: {
            borderLeft: '4px solid #3EA69B',
            background: 'var(--green-green-10, #EBF6F5)',
          },
        },
        error: {
          icon: <SystemIcon name="close-filled" color={Colors.RED100} />,
          style: {
            borderLeft: '4px solid var(--red-red-100, #D50000)',
            background: 'var(--red-red-10, #FAE5E5)',
          },
        },
      }}
    />
  );
};

type Message = ValueOrFunction<Renderable, Toast>;

/**
 * This module provides a set of functions to display different types of toast notifications
 * using the `react-hot-toast` library. It includes predefined styles and icons for success,
 * error, warning, info, and loading toasts.
 *
 * {@link https://react-hot-toast.com/docs}
 * @module Toast
 * @returns {Object} A set of toast functions and a Toaster component to be used in the application.
 */
export const toast = {
  success: (message: Message, options?: ToastOptions) => {
    toastModule.success(message, {
      id: makeId('msgSuccess', formatString(message), options?.id),
      ...options,
    });
  },
  error: (message: Message, options?: ToastOptions) => {
    toastModule.error(message, {
      id: makeId('msgError', formatString(message), options?.id),
      ...options,
    });
  },
  warning: (message: Message, options?: ToastOptions) => {
    toastModule(message, {
      id: makeId('msgWarning', formatString(message), options?.id),
      icon: <SystemIcon name="warning" color={Colors.YELLOW100} />,
      style: {
        borderLeft: '4px solid var(--yellow-yellow-100, #F7D250)',
        background: 'var(--yellow-yellow-10, #FEFAED)',
      },
      ...options,
    });
  },
  info: (message: Message, options?: ToastOptions) => {
    toastModule(message, {
      id: makeId('msgInfo', formatString(message), options?.id),
      icon: <SystemIcon name="information-filled" color={Colors.BLUE100} />,
      style: {
        borderLeft: '4px solid var(--blue-blue-100, #0D47A1)',
        background: 'var(--blue-blue-10, #E6ECF5)',
      },
      ...options,
    });
  },
  loading: (message: Message, options?: ToastOptions) => {
    toastModule.loading(message, options);
  },
  promise: <T,>(
    promise: Promise<T>,
    msgs: {
      loading: Renderable;
      success: ValueOrFunction<Renderable, T>;
      error: ValueOrFunction<Renderable, any>;
    },
    opts?: DefaultToastOptions,
  ) => {
    toastModule.promise(promise, msgs, {
      id: makeId('msg', formatString(msgs.success), opts?.id),
      ...opts,
    });
  },
  dismiss: (id?: string) => {
    toastModule.dismiss(id);
  },
  remove: (id?: string) => {
    toastModule.remove(id);
  },
};
